import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../../layout/components/form/FormLayout.tsx';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    IconButton,
    Link,
    Stack,
} from '@mui/material';
import { Delete, DesignServices } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { Fragment, useState } from 'react';
import {
    AutoActive,
    PermittypeChannels,
} from 'product-shared/product-template/ProductOptions.tsx';
import {
    LabeledSignalisationDownload,
    SignalisationType,
} from '../../common/components/LabeledSignalisationDownload.tsx';
import { OperatorRoutedModalContent } from '../../layout/components/OperatorRoutedModalContent.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    RequestMethod,
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { HeaderCancelSaveButtons } from '../../layout/components/HeaderCancelSaveButtons.tsx';
import { PermitTypeDetailDTO } from './PermitTypes.tsx';
import { IntradayValidityWeekProfile } from 'dg-web-shared/model/IntradayValidityWeekProfile.ts';
import { OperatorQuotaOutlet } from '../../quotas/OperatorQuotasDetail.tsx';
import {
    QuotaInfoType,
    quotaPartitionConfigText,
} from 'dg-web-shared/model/QuotaConfig.ts';
import { QuotaReductionInfo } from '../../product-types/OperatorProductTypesDetailModal.tsx';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { QuotaPartitionInfo } from '../../common/state/PermitTypeState.ts';

export function PermitTypesDetail() {
    const urlParams = useParams<{ permitTypeId: string }>();
    const parsedPermitTypeId = urlParams.permitTypeId
        ? parseInt(urlParams.permitTypeId, 10)
        : NaN;
    const permitTypeId = Number.isNaN(parsedPermitTypeId)
        ? null
        : parsedPermitTypeId;
    const [permitTypeDetailState, refetchPermitTypeDetail] = useServerFetch<
        PermitTypeDetailDTO,
        { permitTypeId: number }
    >(
        ({ permitTypeId }) => ({
            url: `/ui-api/operator-account/permit-type/${permitTypeId}`,
        }),
        permitTypeId !== null ? { permitTypeId: permitTypeId } : null,
    );

    if (permitTypeId === null) {
        return <Navigate to=".." replace />;
    }

    return (
        <>
            <OperatorRoutedModal
                variant={ModalVariant.BIG}
                render={controller => (
                    <OperatorAsyncLoadedSection
                        requestState={permitTypeDetailState}
                        render={permitTypeDetail => {
                            return (
                                <Details
                                    permitType={permitTypeDetail}
                                    onClose={controller.close}
                                    onSaved={refetchPermitTypeDetail}
                                />
                            );
                        }}
                    />
                )}
            />
            <OperatorQuotaOutlet
                context={{ refetchQuota: refetchPermitTypeDetail }}
            />
        </>
    );
}

function Details({
    permitType,
    onClose,
    onSaved,
}: {
    permitType: PermitTypeDetailDTO;
    onClose: () => void;
    onSaved: () => void;
}) {
    const { currentLogin } = useOperatorContext();
    const quotaInfo = permitType.quotaPartitionInfo;
    const [editedValidityDates, setEditedValidityDates] = useState<
        string[] | null
    >(null);
    const [validityDatesState, saveValidityDates] = useServerWrite<
        { withinValidityConstraintDates: string[] },
        null
    >(() => ({
        url: `/ui-api/operator-account/permit-types/${permitType.permitTypeId}`,
        method: RequestMethod.PUT,
    }));
    useServerSuccessEffect(validityDatesState, () => {
        setEditedValidityDates(null);
        onSaved();
    });
    const validityDates =
        editedValidityDates ?? permitType.withinValidityConstraintDates;
    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    Icon={DesignServices}
                    title={permitType.permitTypeDescription}
                    editMode={editedValidityDates !== null}
                    buttons={
                        editedValidityDates !== null ? (
                            <HeaderCancelSaveButtons
                                onCancel={() => setEditedValidityDates(null)}
                                onSave={() =>
                                    saveValidityDates({
                                        withinValidityConstraintDates:
                                            editedValidityDates,
                                    })
                                }
                                loading={
                                    validityDatesState.status ===
                                    RequestStatus.PENDING
                                }
                            />
                        ) : (
                            <Button variant="outlined" onClick={onClose}>
                                <Localized
                                    de="Schliessen"
                                    fr="Fermer"
                                    it="Chiudi"
                                    en="Close"
                                />
                            </Button>
                        )
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bewilligungsart"
                                    fr="Type d'autorisation"
                                    it="Tipo d'autorizzazione"
                                    en="Permit type"
                                />
                            }
                            value={permitType.permitTypeDescription}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Profil Nr"
                                    fr="N° du profil"
                                    it="Nr. profilo"
                                    en="Profile no."
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={
                                permitType.isBadgePermit ? (
                                    <Localized
                                        de="Badge"
                                        fr="Badge"
                                        it="Badge"
                                        en="Badge"
                                    />
                                ) : (
                                    <Localized
                                        de="Kennzeichen"
                                        fr="Immatriculation"
                                        it="Targa"
                                        en="License plate"
                                    />
                                )
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Max. Kennzeichen / Badges"
                                    fr="Max. Immatriculations / Badges"
                                    it="Max. Targhe / Badges"
                                    en="Max. license plates / badges"
                                />
                            }
                            value={permitType.maxIdentificationCount}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zonen"
                                    fr="Zones"
                                    it="Zone"
                                    en="Zones"
                                />
                            }
                            value={
                                permitType.zones.length > 0 ? (
                                    <>
                                        {permitType.zones.map(zone => (
                                            <div key={zone.zoneId}>
                                                {zone.zoneId} | {zone.zoneName}
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    EmDash
                                )
                            }
                        />
                        {permitType.channels ===
                            PermittypeChannels.PARKINGPAY_TWINT_QUICKCHECKOUT &&
                            permitType.urlIdentification && (
                                <LabeledSignalisationDownload
                                    signalisationPdfId={
                                        permitType.urlIdentification
                                    }
                                    type={SignalisationType.PERMIT}
                                />
                            )}
                        {permitType.channels ===
                            PermittypeChannels.PARKINGPAY_TWINT && (
                            <Alert severity="info">
                                <AlertTitle>
                                    <Localized
                                        de="Signalisations-PDF"
                                        fr="PDF de signalisation"
                                        it="PDF segnaletica"
                                        en="Signalisation PDF"
                                    />
                                </AlertTitle>
                                <Localized
                                    de="Die Signalisation nur mit Parkingpay und TWINT ist nicht mehr verfügbar. Bitte kontaktieren Sie unser Parkingportal-Team ("
                                    fr="La signalisation avec seulement Parkingpay et TWINT n'est plus disponible. Veuillez contacter notre équipe Parkingportal ("
                                    it="La segnaletica con solo Parkingpay e TWINT non è più disponibile. Vogliate contattare il nostro team Parkingportal ("
                                    en="The signage with only Parkingpay and TWINT is no longer available. Please contact our Parkingportal team ("
                                />
                                <Link href="mailto:info@parkingportal.ch">
                                    info@parkingportal.ch
                                </Link>
                                <Localized
                                    de=") für ein Upgrade auf Quick-Checkout."
                                    fr=") pour passer à Quick-Checkout."
                                    it=") per effettuare l’aggiornamento a Quick-Checkout."
                                    en=") for an upgrade to Quick-Checkout."
                                />
                            </Alert>
                        )}
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="MWST. Pflicht"
                                    fr="Numéro de TVA"
                                    it="Numero IVA"
                                    en="VAT number"
                                />
                            }
                            value={permitType.vatNumber ?? EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Verkaufsart"
                                    fr="Modalité de vente"
                                    it="Modalità di vendita"
                                    en="Sales channel"
                                />
                            }
                            value={
                                <>
                                    {(permitType.autoActive ===
                                        AutoActive.ALL_CHANNELS ||
                                        permitType.autoActive ===
                                            AutoActive.ALL_CHANNELS_BUT_WHITELIST) && (
                                        <>
                                            <Localized
                                                de="Online"
                                                fr="En ligne"
                                                it="Online"
                                                en="Online"
                                            />
                                            {' / '}
                                        </>
                                    )}
                                    <Localized
                                        de="Schalter"
                                        fr="Guichet"
                                        it="Sportello"
                                        en="Counter"
                                    />
                                </>
                            }
                        />
                        {permitType.showAccount && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Gruppe"
                                        fr="Groupe"
                                        it="Gruppo"
                                        en="Group"
                                    />
                                }
                                value={`${permitType.dtaAccountDescription ?? EmDash}`}
                            />
                        )}
                        {quotaInfo && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Kontingente"
                                        fr="Contingents"
                                        it="Contingenti"
                                        en="Quotas"
                                    />
                                }
                                value={
                                    currentLogin.permissions.quotasWrite ? (
                                        <Link
                                            component={RouterLink}
                                            to={`quotas/${quotaInfo.contractQuotaPartitionId}`}
                                        >
                                            <QuotaConfigLines
                                                quotaInfo={quotaInfo}
                                                isOffstreet={
                                                    permitType.isBadgePermit
                                                }
                                            />
                                        </Link>
                                    ) : (
                                        <QuotaConfigLines
                                            quotaInfo={quotaInfo}
                                            isOffstreet={
                                                permitType.isBadgePermit
                                            }
                                        />
                                    )
                                }
                            />
                        )}
                        {quotaInfo && quotaInfo.otherProducts.length > 0 && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Andere Bewilligungsarten im Kontingent"
                                        fr="Autres types d'autorisation dans le contingent"
                                        it="Altri tipi d'autorizzazione nel contingente"
                                        en="Other permit types in the quota"
                                    />
                                }
                                value={
                                    <>
                                        {quotaInfo.otherProducts.map(
                                            product => (
                                                <div
                                                    key={
                                                        product.contractTemplateId
                                                    }
                                                >
                                                    <Localized
                                                        {...product.name}
                                                    />
                                                </div>
                                            ),
                                        )}
                                    </>
                                }
                            />
                        )}
                        {permitType.intradayValidityWeekProfile && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Tägliche Gültigkeit"
                                        fr="Validité journalière"
                                        it="Validità giornaliera"
                                        en="Daily validity"
                                    />
                                }
                                value={
                                    <IntradayValidityLines
                                        weekProfile={
                                            permitType.intradayValidityWeekProfile
                                        }
                                    />
                                }
                            />
                        )}
                        {validityDates !== null && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Gültigkeitsdaten"
                                        fr="Dates de validité"
                                        it="Date di validità"
                                        en="Validity dates"
                                    />
                                }
                                value={
                                    <ValidityDates
                                        dates={validityDates}
                                        canEdit={
                                            currentLogin.permissions
                                                .userPermissionEdit
                                        }
                                        onChange={setEditedValidityDates}
                                    />
                                }
                            />
                        )}
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function ValidityDates({
    dates,
    canEdit,
    onChange,
}: {
    dates: string[];
    canEdit: boolean;
    onChange: (dates: string[]) => void;
}) {
    return (
        <Stack spacing={1}>
            {dates.length === 0 && !canEdit && EmDash}
            {dates.map(date => (
                <Stack key={date} direction="row" sx={{ alignItems: 'center' }}>
                    {Formatter.dayMonthYear(DateTime.fromISO(date))}
                    {canEdit && (
                        <IconButton
                            size="small"
                            onClick={() =>
                                onChange(dates.filter(d => d !== date))
                            }
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            ))}
            {canEdit && (
                <DesktopDatePicker
                    format="dd.MM.yyyy"
                    value={null}
                    onChange={date => {
                        if (!date || !date.isValid) {
                            return;
                        }
                        const isoDate = Formatter.isoYearMonthDay(date);
                        if (!dates.includes(isoDate)) {
                            onChange([...dates, isoDate].sort());
                        }
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                />
            )}
        </Stack>
    );
}

function QuotaConfigLines({
    quotaInfo,
    isOffstreet,
}: {
    quotaInfo: QuotaPartitionInfo;
    isOffstreet: boolean;
}) {
    return (
        <>
            {quotaInfo.quotaConfigs.map(quotaConfig => (
                <div key={quotaConfig.type}>
                    <Localized
                        {...quotaPartitionConfigText(quotaConfig, isOffstreet)}
                    />
                    {quotaConfig.type ===
                        QuotaInfoType.CONTRACT_PURCHASE_OVERLAPPING && (
                        <QuotaReductionInfo
                            quotaLimit={quotaConfig.limit}
                            quotaReductions={quotaInfo.quotaReductions}
                        />
                    )}
                </div>
            ))}
        </>
    );
}

const weekDays: {
    key: keyof IntradayValidityWeekProfile;
    label: { de: string; fr: string; it: string; en: string };
}[] = [
    { key: 'monday', label: { de: 'Mo', fr: 'Lu', it: 'Lu', en: 'Mo' } },
    { key: 'tuesday', label: { de: 'Di', fr: 'Ma', it: 'Ma', en: 'Tu' } },
    { key: 'wednesday', label: { de: 'Mi', fr: 'Me', it: 'Me', en: 'We' } },
    { key: 'thursday', label: { de: 'Do', fr: 'Je', it: 'Gi', en: 'Th' } },
    { key: 'friday', label: { de: 'Fr', fr: 'Ve', it: 'Ve', en: 'Fr' } },
    { key: 'saturday', label: { de: 'Sa', fr: 'Sa', it: 'Sa', en: 'Sa' } },
    { key: 'sunday', label: { de: 'So', fr: 'Di', it: 'Do', en: 'Su' } },
];

function IntradayValidityLines({
    weekProfile,
}: {
    weekProfile: IntradayValidityWeekProfile;
}) {
    const groups = groupDaysWithSameTimes(weekProfile);
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 2,
            }}
        >
            {groups.map(group => (
                <Fragment key={group.first.key}>
                    <Box>
                        {groups.length === 1 ? (
                            <Localized
                                de="Täglich"
                                fr="Tous les jours"
                                it="Ogni giorno"
                                en="Daily"
                            />
                        ) : group.first.key === group.last.key ? (
                            <Localized {...group.first.label} />
                        ) : (
                            <>
                                <Localized {...group.first.label} />
                                {'–'}
                                <Localized {...group.last.label} />
                            </>
                        )}
                    </Box>
                    <Box>
                        {group.times ?? (
                            <Localized
                                de="nicht gültig"
                                fr="non valable"
                                it="non valido"
                                en="not valid"
                            />
                        )}
                    </Box>
                </Fragment>
            ))}
        </Box>
    );
}

function groupDaysWithSameTimes(weekProfile: IntradayValidityWeekProfile) {
    const groups: {
        first: (typeof weekDays)[number];
        last: (typeof weekDays)[number];
        times: string | null;
    }[] = [];
    for (const day of weekDays) {
        const entries = weekProfile[day.key];
        const times =
            entries.length > 0
                ? entries.map(entry => `${entry.start}–${entry.end}`).join(', ')
                : null;
        const previous = groups[groups.length - 1];
        if (previous && previous.times === times) {
            previous.last = day;
        } else {
            groups.push({ first: day, last: day, times });
        }
    }
    return groups;
}
